/**
 * Stats Counter — Animated number counting on scroll
 *
 * Architecture:
 *
 * 1. Viewport detection (IntersectionObserver)
 *    When the section enters the viewport (20% visible),
 *    all .stats-counter__value elements begin counting
 *    simultaneously from 0 to their data-target value.
 *    The observer disconnects immediately — one-shot animation.
 *
 * 2. Counting animation (requestAnimationFrame)
 *    Each frame calculates elapsed progress (0 → 1) and applies
 *    an ease-out cubic curve: t = 1 - (1 - t)^3
 *    The eased value maps to the numeric range [0, target].
 *    Duration: 3.5 seconds (configurable via data-duration).
 *
 * 3. Decimal handling
 *    If the target contains a decimal (e.g. "3.5"), the counter
 *    interpolates with one decimal place. Otherwise integers only.
 *
 * 4. Reduced motion
 *    If prefers-reduced-motion: reduce is active, all values
 *    are set to their targets immediately — no animation.
 */

if (!customElements.get('stats-counter')) {
  customElements.define(
    'stats-counter',
    class StatsCounter extends HTMLElement {
      /** @type {HTMLElement[]} */
      values = [];

      /** @type {IntersectionObserver|null} */
      observer = null;

      /** @type {number} Animation duration in ms */
      duration = 3500;

      /** @type {boolean} */
      hasAnimated = false;

      /* ── Lifecycle ─────────────────────────────────────────────── */

      connectedCallback() {
        this.values = [...this.querySelectorAll('.stats-counter__value')];
        if (!this.values.length) return;

        /* Read custom duration if provided */
        const customDuration = parseInt(this.dataset.duration, 10);
        if (customDuration > 0) this.duration = customDuration;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.showFinalValues();
          return;
        }

        this.setupObserver();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      /* ── Viewport detection ────────────────────────────────────── */

      setupObserver() {
        this.observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting && !this.hasAnimated) {
                this.hasAnimated = true;
                this.animateAll();
                this.observer.disconnect();
              }
            }
          },
          { threshold: 0.2 }
        );
        this.observer.observe(this);
      }

      /* ── Counting animation ────────────────────────────────────── */

      animateAll() {
        const start = performance.now();

        const tick = (now) => {
          const elapsed = now - start;
          const progress = Math.min(elapsed / this.duration, 1);

          /* Ease-out cubic: fast start → gentle deceleration */
          const eased = 1 - Math.pow(1 - progress, 3);

          for (const el of this.values) {
            const target = parseFloat(el.dataset.target) || 0;
            const isDecimal = el.dataset.target.includes('.');
            const current = eased * target;

            el.textContent = isDecimal
              ? current.toFixed(1)
              : Math.round(current).toString();
          }

          if (progress < 1) {
            requestAnimationFrame(tick);
          } else {
            /* Ensure final values are exact */
            this.showFinalValues();
          }
        };

        requestAnimationFrame(tick);
      }

      /* ── Set all values to their targets instantly ──────────────── */

      showFinalValues() {
        for (const el of this.values) {
          el.textContent = el.dataset.target;
        }
      }
    }
  );
}
